/**
 * ❤️ « J'aime » anonyme + « Partager » sur une actualité.
 *
 * Le « j'aime » ne demande pas de compte : un identifiant d'appareil aléatoire
 * (localStorage, aucune donnée personnelle) évite les doubles votes depuis le
 * même téléphone. Les clients qui n'aiment pas être affichés sur Facebook
 * peuvent réagir sans que personne ne le voie.
 *
 * Le partage passe par la feuille native du téléphone (WhatsApp, Messages,
 * Facebook…) quand elle existe, sinon par un petit menu. Le lien partagé est
 * `/a/<id>` : une page d'aperçu (titre, image, résumé) lue par les robots
 * de WhatsApp/Facebook, qui renvoie ensuite vers l'actualité.
 */
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../../services/api';
import './LikeShareBar.css';

const DEVICE_KEY = 'folies_device';

export function getDeviceToken(): string {
  try {
    let token = localStorage.getItem(DEVICE_KEY);
    if (!token) {
      token =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(DEVICE_KEY, token);
    }
    return token;
  } catch {
    return 'no-storage';
  }
}

interface LikeShareBarProps {
  publicationId: string;
  title: string;
  likesCount: number;
  liked?: boolean;
  sharesCount?: number;
  /** compact = sur la carte du fil (stoppe la propagation du clic) */
  compact?: boolean;
  onChange?: (state: { liked: boolean; likes_count: number }) => void;
}

const LikeShareBar: React.FC<LikeShareBarProps> = ({
  publicationId,
  title,
  likesCount,
  liked: likedProp = false,
  compact = false,
  onChange,
}) => {
  const [liked, setLiked] = useState(likedProp);
  const [count, setCount] = useState(likesCount);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLiked(likedProp);
    setCount(likesCount);
  }, [likedProp, likesCount, publicationId]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const shareUrl = `${window.location.origin}/a/${publicationId}`;

  const stop = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const toggleLike = async (e: React.MouseEvent) => {
    stop(e);
    if (busy) return;
    // Optimiste : le cœur réagit tout de suite, le serveur confirme.
    const nextLiked = !liked;
    setLiked(nextLiked);
    setCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));
    setBusy(true);
    try {
      const r = await api.post(`/publications/${publicationId}/like`, { device_token: getDeviceToken() });
      setLiked(r.liked);
      setCount(r.likes_count);
      onChange?.({ liked: r.liked, likes_count: r.likes_count });
    } catch {
      setLiked(!nextLiked);
      setCount((c) => Math.max(0, c + (nextLiked ? -1 : 1)));
    } finally {
      setBusy(false);
    }
  };

  const track = (channel: string) => {
    api.post(`/publications/${publicationId}/share`, { channel }).catch(() => undefined);
  };

  const share = async (e: React.MouseEvent) => {
    stop(e);
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title, text: `${title} — Les Folies Maraîchères`, url: shareUrl });
        track('native');
      } catch {
        /* annulé par l'utilisateur */
      }
      return;
    }
    setMenuOpen((o) => !o);
  };

  const copy = async (e: React.MouseEvent) => {
    stop(e);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      track('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copiez ce lien :', shareUrl);
    }
  };

  const text = encodeURIComponent(`${title} — Les Folies Maraîchères`);
  const url = encodeURIComponent(shareUrl);

  return (
    <div className={`like-share-bar ${compact ? 'compact' : ''}`} onClick={stop}>
      <button
        type="button"
        className={`btn-like ${liked ? 'liked' : ''}`}
        onClick={toggleLike}
        aria-pressed={liked}
        aria-label={liked ? "Je n'aime plus" : "J'aime"}
        title={liked ? "Je n'aime plus" : "J'aime"}
      >
        <span className="heart" aria-hidden="true">{liked ? '❤️' : '🤍'}</span>
        <span className="like-count">{count}</span>
      </button>

      <div className="share-wrap" ref={menuRef}>
        <button type="button" className="btn-share-bar" onClick={share} aria-label="Partager" title="Partager">
          <span aria-hidden="true">↗</span> Partager
        </button>
        {menuOpen && (
          <div className="share-menu" role="menu">
            <button type="button" role="menuitem" onClick={copy}>
              {copied ? '✓ Lien copié' : '🔗 Copier le lien'}
            </button>
            <a role="menuitem" href={`https://wa.me/?text=${text}%20${url}`} target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp')}>
              💬 WhatsApp
            </a>
            <a role="menuitem" href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" onClick={() => track('facebook')}>
              📘 Facebook
            </a>
            <a role="menuitem" href={`mailto:?subject=${text}&body=${url}`} onClick={() => track('email')}>
              ✉️ Email
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default LikeShareBar;
